import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Loader2, MapPin, Wifi, Pencil, Check, X, Bot, Activity as ActivityIcon, Maximize2, Eye, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { KES, formatDate, formatBytes, formatRelative } from "@/lib/format";
import { getChartTheme } from "@/lib/chartTheme";
import { useAdminTheme } from "@/context/AdminThemeContext";
import { OSM_TILE_URL, OSM_ATTRIBUTION, useMapProvider, useGoogleMapsSettings, MapProviderToggle } from "@/lib/mapProvider";
import GoogleCoordinatePicker from "@/components/GoogleCoordinatePicker";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const statusStyles = {
  active: "text-nhp-primary border-nhp-primary/40 bg-nhp-primary/10",
  pending_install: "text-nhp-info border-nhp-info/40 bg-nhp-info/10",
  inactive: "text-nhp-muted border-nhp-border", // customer-level only
  paused: "text-nhp-muted border-nhp-border", // service-level only
  suspended: "text-nhp-warning border-nhp-warning/40 bg-nhp-warning/10", // both customer- and service-level
  disabled: "text-nhp-critical border-nhp-critical/40 bg-nhp-critical/10",
  terminated: "text-nhp-critical border-nhp-critical/40 bg-nhp-critical/10",
  archived: "text-nhp-muted border-nhp-border",
};

// Only "suspend_if_unpaid" needs a friendlier label than its raw value --
// every other target_status already reads fine as plain text.
const TARGET_STATUS_LABEL = { suspend_if_unpaid: "Suspend if still unpaid" };

const DEFAULT_CENTER = [-1.286389, 36.817223]; // Nairobi fallback when no coordinates set yet

// Same divIcon technique as PppoeMap.js -- sidesteps react-leaflet's default
// marker-icon bundling gotcha entirely, and works fine on a draggable marker.
const pinIcon = L.divIcon({
  className: "",
  html: `<div style="width:16px;height:16px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#00E676;border:2px solid rgba(0,0,0,0.35)"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 16],
});

function MapClickHandler({ onPick }) {
  useMapEvents({ click(e) { onPick(e.latlng.lat, e.latlng.lng); } });
  return null;
}

/** Shared by the small inline picker and the expanded dialog -- rendered as
 * two separate MapContainer instances (one mounts fresh when the dialog
 * opens) rather than trying to resize one Leaflet instance in place, which
 * needs an explicit invalidateSize() call and is easy to get subtly wrong. */
function CoordinateMap({ theme, mapCenter, hasCoords, zoom, height, onPick, onDrag }) {
  return (
    <div style={{ height }} className={`border border-nhp-border ${theme === "dark" ? "leaflet-invert-tiles" : ""}`}>
      <MapContainer center={mapCenter} zoom={zoom} style={{ height: "100%", width: "100%" }}>
        <TileLayer url={OSM_TILE_URL} attribution={OSM_ATTRIBUTION} />
        <MapClickHandler onPick={onPick} />
        {hasCoords && (
          <Marker position={mapCenter} icon={pinIcon} draggable eventHandlers={{ dragend: (e) => onDrag(e.target.getLatLng()) }} />
        )}
      </MapContainer>
    </div>
  );
}

/** Renders the Leaflet or Google picker depending on the admin's chosen
 * provider — the two libraries don't share a rendering model, so this is a
 * plain switch rather than a unified component. */
function PickerMap({ provider, apiKey, theme, mapCenter, hasCoords, zoom, height, onPick, onDrag }) {
  if (provider === "google") {
    return <GoogleCoordinatePicker apiKey={apiKey} mapCenter={mapCenter} hasCoords={hasCoords} zoom={zoom} height={height} onPick={onPick} onDrag={onDrag} />;
  }
  return <CoordinateMap theme={theme} mapCenter={mapCenter} hasCoords={hasCoords} zoom={zoom} height={height} onPick={onPick} onDrag={onDrag} />;
}

function Field({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">{label}</div>
      <div className="text-sm mt-1">{value ?? "—"}</div>
    </div>
  );
}

/** Turns one audit-log entry into a human-readable phrase for the activity feed. */
function describeActivity(a) {
  const d = a.details || {};
  switch (a.action) {
    case "created": return `created this account (${d.plan_name || "plan"})`;
    case "provisioned": return "provisioned the account — credentials pushed";
    case "suspended": return d.reason === "non-payment" ? "suspended it for non-payment"
      : d.reason === "customer_suspended" ? "suspended it (customer suspended)"
      : d.reason === "grace_period_expired" ? "re-suspended it — grace period ended still unpaid"
      : "suspended it";
    case "reactivated": return "reactivated it";
    case "paused": return "paused billing (service stays connected)";
    case "disabled": return "disabled it and released its IP address";
    case "price_changed": return d.to != null ? `set a custom price of ${KES(d.to)}` : "reverted to the plan's price";
    case "location_changed": return d.to ? `set location to ${d.to}` : "cleared the assigned location";
    case "payment_recorded": return `recorded a payment of ${KES(d.amount_kes)}${d.via ? ` (${d.via})` : ""}`;
    case "imported": return `imported this account from UCRM (${d.invoices_imported ?? 0} invoices)`;
    case "contact_updated": return `updated ${Object.keys(d).join(", ")}`;
    case "coordinates_changed": return "updated GPS coordinates";
    case "status_synced": return d.planned
      ? `applied a planned status change (${d.from} → ${d.to})`
      : `status changed from ${d.from} to ${d.to} (synced from UCRM)`;
    case "archived": return "archived it";
    case "service_updated": return "updated the service (plan/IP/address/grace period)";
    case "portal_password_reset": return "generated a new portal password";
    case "status_change_planned": return `scheduled a change to ${TARGET_STATUS_LABEL[d.target_status] || d.target_status} on ${d.effective_date}`;
    case "status_change_cancelled": return `cancelled a planned change to ${d.target_status}`;
    default: return a.action;
  }
}

export default function PppoeAccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [activity, setActivity] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("overview");
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageDays, setUsageDays] = useState(30);
  const [sessions, setSessions] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [editingLocation, setEditingLocation] = useState(false);
  const [locationInput, setLocationInput] = useState("");
  const [savingLocation, setSavingLocation] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [contactForm, setContactForm] = useState({ phone: "", email: "", full_name: "", id_number: "" });
  const [savingContact, setSavingContact] = useState(false);
  const [coords, setCoords] = useState({ lat: "", lng: "" });
  const [savingCoords, setSavingCoords] = useState(false);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [mapProvider, setMapProvider] = useMapProvider();
  const googleMaps = useGoogleMapsSettings();
  const [ipAddress, setIpAddress] = useState(null);
  const [portalPasswordSet, setPortalPasswordSet] = useState(false);
  const [portalPassword, setPortalPassword] = useState(null);
  const [loadingPortalPassword, setLoadingPortalPassword] = useState(false);
  const [regeneratingPortalPassword, setRegeneratingPortalPassword] = useState(false);
  const [plannedChanges, setPlannedChanges] = useState([]);
  const [plannedDialogOpen, setPlannedDialogOpen] = useState(false);
  const [plannedForm, setPlannedForm] = useState({ target_status: "suspended", effective_date: "", note: "" });
  const [savingPlannedChange, setSavingPlannedChange] = useState(false);
  const [plans, setPlans] = useState([]);
  const [addServiceOpen, setAddServiceOpen] = useState(false);
  const [addServiceForm, setAddServiceForm] = useState({
    plan_id: "", pppoe_username: "", static_ip: "", installation_address: "", grace_period_days: "", custom_price_kes: "",
  });
  const [savingAddService, setSavingAddService] = useState(false);
  const [editServiceId, setEditServiceId] = useState(null);
  const [editServiceForm, setEditServiceForm] = useState({ plan_id: "", static_ip: "", installation_address: "", grace_period_days: "", custom_price_kes: "", status: "" });
  const [editServiceOriginalStatus, setEditServiceOriginalStatus] = useState("");
  const [savingServiceEdit, setSavingServiceEdit] = useState(false);
  const [pauseStartAt, setPauseStartAt] = useState("");
  const [pauseResumeAt, setPauseResumeAt] = useState("");
  const [pauseResumeTo, setPauseResumeTo] = useState("active");
  const [suspendPostponeUntil, setSuspendPostponeUntil] = useState("");
  const [deactivatingCustomer, setDeactivatingCustomer] = useState(false);
  const [suspendingCustomer, setSuspendingCustomer] = useState(false);
  const [reactivatingCustomer, setReactivatingCustomer] = useState(false);
  const [deleteServicesConfirmOpen, setDeleteServicesConfirmOpen] = useState(false);
  const [deletingServices, setDeletingServices] = useState(false);
  const { theme } = useAdminTheme();
  const chart = getChartTheme(theme);

  const load = async () => {
    try {
      const [{ data }, activityRes] = await Promise.all([
        api.get(`/admin/pppoe/accounts/${id}/profile`),
        api.get(`/admin/pppoe/accounts/${id}/activity`).catch(() => ({ data: [] })),
      ]);
      setProfile(data);
      setActivity(activityRes.data);
      setCoords({ lat: data.customer?.gps_lat ?? "", lng: data.customer?.gps_lng ?? "" });
      setIpAddress(data.ip_address || null);
      setPortalPasswordSet(!!data.portal_password_set);
      setPortalPassword(null);
      setPlannedChanges(data.planned_status_changes || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    api.get("/admin/pppoe/locations").then(({ data }) => setLocations(data)).catch(() => {});
    api.get("/admin/pppoe/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (googleMaps.loaded && !googleMaps.valid && mapProvider === "google") setMapProvider("osm");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleMaps.loaded, googleMaps.valid]);

  useEffect(() => {
    if (tab !== "usage") return;
    setUsageLoading(true);
    setSessionsLoading(true);
    api.get(`/admin/pppoe/accounts/${id}/usage`, { params: { days: usageDays } })
      .then(({ data }) => setUsage(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setUsageLoading(false));
    api.get(`/admin/pppoe/accounts/${id}/sessions`, { params: { days: usageDays } })
      .then(({ data }) => setSessions(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setSessionsLoading(false));
  }, [tab, id, usageDays]);

  const runAction = async (action) => {
    setBusy(true);
    try {
      await api.post(`/admin/pppoe/accounts/${id}/${action}`);
      toast.success(action === "provision" ? "Provisioned — credentials pushed" : "Done");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  // The Edit Service dialog's Status field -- each target status maps to a
  // distinct guarded backend action, never a raw field write, so
  // RADIUS/network side effects always stay consistent with the status.
  const SERVICE_STATUS_ENDPOINT = { active: "reactivate", paused: "pause", suspended: "suspend", disabled: "disable" };

  // "now", as a value the datetime-local input understands, in the
  // viewer's own timezone (toISOString is always UTC otherwise).
  const nowForDatetimeLocal = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };

  // A <input type="datetime-local"> value ("2026-09-18T22:33") carries no
  // timezone -- it's the admin's own local wall-clock time (EAT here), but
  // has no "Z" or offset to say so. new Date() on a date-TIME string with
  // no offset is specified to parse as local time, so this converts it to
  // a real UTC instant before it's sent -- skipping this step is exactly
  // what previously stored "22:33 EAT" as if it were "22:33 UTC" (an
  // effective 3-hour shift into the future).
  const toUtcIso = (datetimeLocalValue) => (datetimeLocalValue ? new Date(datetimeLocalValue).toISOString() : null);

  const revealPortalPassword = async () => {
    if (portalPassword != null) { setPortalPassword(null); return; } // toggle hide
    setLoadingPortalPassword(true);
    try {
      const { data } = await api.get(`/admin/pppoe/accounts/${id}/portal-password`);
      setPortalPassword(data.password || "");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoadingPortalPassword(false); }
  };

  const regeneratePortalPassword = async () => {
    setRegeneratingPortalPassword(true);
    try {
      const { data } = await api.post(`/admin/pppoe/accounts/${id}/portal-password/regenerate`);
      setPortalPassword(data.password);
      setPortalPasswordSet(true);
      toast.success("New portal password generated");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setRegeneratingPortalPassword(false); }
  };

  const savePlannedChange = async () => {
    if (!plannedForm.effective_date) { toast.error("Pick a date"); return; }
    setSavingPlannedChange(true);
    try {
      await api.post(`/admin/pppoe/accounts/${id}/planned-status-changes`, {
        ...plannedForm, effective_date: toUtcIso(plannedForm.effective_date),
      });
      toast.success("Status change scheduled");
      setPlannedForm({ target_status: "suspended", effective_date: "", note: "" });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSavingPlannedChange(false); }
  };

  const cancelPlannedChange = async (changeId) => {
    try {
      await api.delete(`/admin/pppoe/accounts/${id}/planned-status-changes/${changeId}`);
      toast.success("Planned change removed");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const createService = async () => {
    if (!addServiceForm.plan_id || !addServiceForm.pppoe_username) { toast.error("Pick a plan and enter a PPPoE username"); return; }
    setSavingAddService(true);
    try {
      await api.post("/admin/pppoe/accounts", {
        phone: customer.phone,
        plan_id: addServiceForm.plan_id,
        pppoe_username: addServiceForm.pppoe_username,
        static_ip: addServiceForm.static_ip || null,
        installation_address: addServiceForm.installation_address || null,
        grace_period_days: addServiceForm.grace_period_days ? Number(addServiceForm.grace_period_days) : undefined,
        custom_price_kes: addServiceForm.custom_price_kes ? Number(addServiceForm.custom_price_kes) : null,
      });
      toast.success("Service added");
      setAddServiceOpen(false);
      setAddServiceForm({ plan_id: "", pppoe_username: "", static_ip: "", installation_address: "", grace_period_days: "", custom_price_kes: "" });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSavingAddService(false); }
  };

  const openEditService = (svc) => {
    setEditServiceForm({
      plan_id: svc.plan_id, static_ip: svc.static_ip || "",
      installation_address: svc.installation_address || "", grace_period_days: String(svc.grace_period_days ?? 3),
      custom_price_kes: svc.custom_price_kes != null ? String(svc.custom_price_kes) : "",
      status: svc.status,
    });
    setEditServiceOriginalStatus(svc.status);
    setPauseStartAt(nowForDatetimeLocal());
    setPauseResumeAt("");
    setPauseResumeTo("active");
    setSuspendPostponeUntil("");
    setEditServiceId(svc.id);
  };

  const saveServiceEdit = async () => {
    setSavingServiceEdit(true);
    try {
      await api.patch(`/admin/pppoe/accounts/${editServiceId}/service`, {
        plan_id: editServiceForm.plan_id,
        static_ip: editServiceForm.static_ip || null,
        installation_address: editServiceForm.installation_address || null,
        grace_period_days: Number(editServiceForm.grace_period_days),
        custom_price_kes: editServiceForm.custom_price_kes ? Number(editServiceForm.custom_price_kes) : null,
      });

      if (editServiceForm.status !== editServiceOriginalStatus) {
        if (editServiceForm.status === "paused") {
          // A future start creates a planned change instead of pausing right
          // away; an optional auto-resume is a second planned change --
          // nhp_pppoe_planned_status_changes already supports any number of
          // scheduled entries per account, so this is just two of them.
          const startsNow = !pauseStartAt || new Date(pauseStartAt) <= new Date();
          if (startsNow) {
            await api.post(`/admin/pppoe/accounts/${editServiceId}/pause`);
          } else {
            await api.post(`/admin/pppoe/accounts/${editServiceId}/planned-status-changes`, {
              target_status: "paused", effective_date: toUtcIso(pauseStartAt),
            });
          }
          if (pauseResumeAt) {
            await api.post(`/admin/pppoe/accounts/${editServiceId}/planned-status-changes`, {
              target_status: pauseResumeTo, effective_date: toUtcIso(pauseResumeAt),
            });
          }
        } else if (editServiceForm.status === "disabled") {
          if (!window.confirm("Disable this service? Its network access is denied and its IP address is released for reuse elsewhere.")) {
            setSavingServiceEdit(false);
            return;
          }
          await api.post(`/admin/pppoe/accounts/${editServiceId}/disable`);
        } else {
          const endpoint = SERVICE_STATUS_ENDPOINT[editServiceForm.status];
          if (endpoint) await api.post(`/admin/pppoe/accounts/${editServiceId}/${endpoint}`);
        }
      }

      toast.success("Service updated");
      setEditServiceId(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSavingServiceEdit(false); }
  };

  const stopSuspensionNow = async () => {
    if (!window.confirm("Stop the suspension now, permanently? This service becomes Active immediately with no future re-suspend scheduled.")) return;
    setSavingServiceEdit(true);
    try {
      await api.post(`/admin/pppoe/accounts/${editServiceId}/reactivate`);
      toast.success("Suspension stopped — service is active");
      setEditServiceId(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSavingServiceEdit(false); }
  };

  // "Grant a grace period" -- the customer says they'll pay by end of day,
  // so reactivate them right now, but automatically re-suspend on the
  // deadline UNLESS they've actually paid by then (checked live at that
  // moment, not decided now) -- a plain "become active later" wouldn't
  // give them access today, and a plain "become suspended later" would
  // suspend them regardless of whether they paid.
  const postponeSuspension = async () => {
    if (!suspendPostponeUntil) { toast.error("Pick a date and time"); return; }
    setSavingServiceEdit(true);
    try {
      await api.post(`/admin/pppoe/accounts/${editServiceId}/reactivate`);
      await api.post(`/admin/pppoe/accounts/${editServiceId}/planned-status-changes`, {
        target_status: "suspend_if_unpaid", effective_date: toUtcIso(suspendPostponeUntil),
      });
      toast.success("Reactivated now — will auto-suspend on the deadline only if still unpaid");
      setEditServiceId(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSavingServiceEdit(false); }
  };

  const deactivateCustomer = async () => {
    setDeactivatingCustomer(true);
    try {
      const { data } = await api.post(`/admin/pppoe/customers/${customer.id}/deactivate`);
      toast.success(`Customer marked inactive — ${data.archived_count} service(s) archived`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setDeactivatingCustomer(false); }
  };

  const suspendCustomerAction = async () => {
    setSuspendingCustomer(true);
    try {
      const { data } = await api.post(`/admin/pppoe/customers/${customer.id}/suspend`);
      toast.success(`Customer suspended — ${data.blocked_count} service(s) blocked`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSuspendingCustomer(false); }
  };

  const reactivateCustomer = async () => {
    setReactivatingCustomer(true);
    try {
      await api.post(`/admin/pppoe/customers/${customer.id}/reactivate`);
      toast.success("Customer reactivated");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setReactivatingCustomer(false); }
  };

  const deleteAllServices = async () => {
    setDeletingServices(true);
    try {
      const { data } = await api.delete(`/admin/pppoe/customers/${customer.id}/services`);
      toast.success(`${data.deleted_count} service(s) permanently deleted`);
      // This account (the page's own URL param) was just deleted along with
      // every other service this customer held -- nothing left to reload.
      navigate("/admin/pppoe-accounts");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); setDeletingServices(false); }
  };

  const saveLocation = async (locationId) => {
    setSavingLocation(true);
    try {
      await api.patch(`/admin/pppoe/accounts/${id}/location`, { location_id: locationId });
      toast.success(locationId == null ? "Location cleared" : "Location assigned");
      setEditingLocation(false);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSavingLocation(false); }
  };

  const saveContact = async () => {
    setSavingContact(true);
    try {
      await api.patch(`/admin/pppoe/accounts/${id}/contact`, contactForm);
      toast.success("Contact details saved");
      setEditingContact(false);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSavingContact(false); }
  };

  const saveCoordinates = async () => {
    setSavingCoords(true);
    try {
      await api.patch(`/admin/pppoe/accounts/${id}/coordinates`, {
        gps_lat: coords.lat !== "" ? Number(coords.lat) : null,
        gps_lng: coords.lng !== "" ? Number(coords.lng) : null,
      });
      toast.success("Coordinates saved");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSavingCoords(false); }
  };

  const recordPayment = async (invoiceId) => {
    if (!window.confirm("Record this invoice as paid (cash/bank, outside M-Pesa)?")) return;
    try {
      await api.post(`/admin/pppoe/invoices/${invoiceId}/record-payment`, {});
      toast.success("Payment recorded");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  if (!profile) {
    return (
      <div>
        <PageHeader testId="pppoe-detail-header" title="PPPoE Account" subtitle="Loading…" />
        <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-nhp-muted" /></div>
      </div>
    );
  }

  const { account, customer, plan, invoices, usage_30d, services = [] } = profile;
  const isLegacy = account.source === "legacy_import";
  const chartSeries = (usage?.series || []).map((d) => ({
    day: d.day,
    download_mb: Number(d.download_bytes || 0) / (1024 * 1024),
    upload_mb: Number(d.upload_bytes || 0) / (1024 * 1024),
  }));
  const hasCoords = coords.lat !== "" && coords.lng !== "" && !Number.isNaN(Number(coords.lat)) && !Number.isNaN(Number(coords.lng));
  const mapCenter = hasCoords ? [Number(coords.lat), Number(coords.lng)] : DEFAULT_CENTER;

  return (
    <div>
      <PageHeader
        testId="pppoe-detail-header"
        title={account.pppoe_username}
        subtitle={customer?.full_name ? `${customer.full_name} — ${customer.phone}` : customer?.phone}
        actions={
          <>
            <Link to="/admin/pppoe-accounts">
              <Button variant="ghost" className="h-10 rounded-none font-mono text-xs">
                <ArrowLeft className="h-4 w-4 mr-2" /> BACK
              </Button>
            </Link>
            <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${statusStyles[account.status] || "text-nhp-muted border-nhp-border"}`}>
              {account.status.replace("_", " ")}
            </span>
            {!isLegacy && account.status === "pending_install" && (
              <Button size="sm" disabled={busy} onClick={() => runAction("provision")} className="h-9 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary text-xs">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "PROVISION"}
              </Button>
            )}
          </>
        }
      />

      <div className="p-4 md:p-8 space-y-4">
        {isLegacy && (
          <div className="border border-nhp-info/40 bg-nhp-info/10 text-nhp-info text-xs font-mono px-4 py-3">
            Imported from UCRM for visibility — billing and suspension for this customer are managed in UCRM, not here.
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-4 items-start">
          <div className="flex-1 min-w-0 w-full space-y-4">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-nhp-surface border border-nhp-border rounded-none h-10">
                <TabsTrigger value="overview" className="rounded-none data-[state=active]:bg-nhp-primary data-[state=active]:text-nhp-on-primary">Overview</TabsTrigger>
                <TabsTrigger value="services" className="rounded-none data-[state=active]:bg-nhp-primary data-[state=active]:text-nhp-on-primary">Services</TabsTrigger>
                <TabsTrigger value="billing" className="rounded-none data-[state=active]:bg-nhp-primary data-[state=active]:text-nhp-on-primary">Billing</TabsTrigger>
                <TabsTrigger value="usage" className="rounded-none data-[state=active]:bg-nhp-primary data-[state=active]:text-nhp-on-primary">Usage</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-4">
                <div className="border border-nhp-border bg-nhp-surface p-4 md:p-6 space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Contact</div>
                    {!editingContact && (
                      <button
                        type="button"
                        onClick={() => {
                          setContactForm({
                            phone: customer?.phone || "", email: customer?.email || "",
                            full_name: customer?.full_name || "", id_number: customer?.id_number || "",
                          });
                          setEditingContact(true);
                        }}
                        className="text-nhp-muted hover:text-nhp-text"
                        title="Edit contact"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  {editingContact ? (
                    <div className="grid grid-cols-2 gap-3">
                      <Input placeholder="Phone" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} className="h-9 bg-nhp-bg border-nhp-border rounded-none font-mono text-sm" />
                      <Input placeholder="Email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} className="h-9 bg-nhp-bg border-nhp-border rounded-none text-sm" />
                      <Input placeholder="Full name" value={contactForm.full_name} onChange={(e) => setContactForm({ ...contactForm, full_name: e.target.value })} className="h-9 bg-nhp-bg border-nhp-border rounded-none text-sm" />
                      <Input placeholder="ID number" value={contactForm.id_number} onChange={(e) => setContactForm({ ...contactForm, id_number: e.target.value })} className="h-9 bg-nhp-bg border-nhp-border rounded-none font-mono text-sm" />
                      <div className="col-span-2 flex gap-2">
                        <Button size="sm" disabled={savingContact} onClick={saveContact} className="h-9 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary text-xs">
                          {savingContact ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="h-3.5 w-3.5 mr-1" /> SAVE</>}
                        </Button>
                        <Button size="sm" variant="ghost" disabled={savingContact} onClick={() => setEditingContact(false)} className="h-9 rounded-none text-xs">
                          <X className="h-3.5 w-3.5 mr-1" /> CANCEL
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Field label="Phone" value={customer?.phone} />
                      <Field label="Email" value={customer?.email} />
                      <Field label="Full name" value={customer?.full_name} />
                      <Field label="ID Number" value={customer?.id_number} />
                    </div>
                  )}

                  {!isLegacy && (
                    <div className="pt-4 border-t border-nhp-border">
                      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Status</label>
                      <div className="flex items-center gap-2 mt-1">
                        <select
                          value={["inactive", "suspended"].includes(customer?.status) ? customer.status : "active"}
                          disabled={deactivatingCustomer || suspendingCustomer || reactivatingCustomer}
                          onChange={(e) => {
                            if (e.target.value === "inactive") {
                              if (window.confirm("Mark this customer inactive? Every active PPPoE service they hold will be archived, and they'll be locked out of the customer portal. Use this when they can no longer pay.")) deactivateCustomer();
                            } else if (e.target.value === "suspended") {
                              if (window.confirm("Suspend this customer? Every active PPPoE service they hold will be blocked (network access denied, reversible).")) suspendCustomerAction();
                            } else {
                              reactivateCustomer();
                            }
                          }}
                          className="h-10 flex-1 min-w-0 bg-nhp-bg border border-nhp-border px-3 text-sm"
                        >
                          <option value="active">Active</option>
                          <option value="suspended">Suspended</option>
                          <option value="inactive">Inactive</option>
                        </select>
                        <button
                          type="button" onClick={() => setPlannedDialogOpen(true)} title="Planned change status"
                          className={`relative h-10 w-10 shrink-0 grid place-items-center border border-nhp-border hover:bg-nhp-bg ${plannedChanges.length ? "text-nhp-primary" : "text-nhp-muted"}`}
                        >
                          <CalendarClock className="h-4 w-4" />
                          {plannedChanges.length > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-nhp-primary text-nhp-on-primary text-[9px] font-bold grid place-items-center">
                              {plannedChanges.length}
                            </span>
                          )}
                        </button>
                      </div>
                      {customer?.status === "inactive" && (
                        <Button
                          size="sm" variant="outline" onClick={() => setDeleteServicesConfirmOpen(true)}
                          className="h-8 rounded-none text-[10px] text-nhp-critical border-nhp-critical/40 hover:bg-nhp-critical/10 mt-2"
                        >
                          DELETE ALL SERVICES
                        </Button>
                      )}
                      {plannedChanges.length > 0 && (
                        <div className="space-y-1 mt-2">
                          {plannedChanges.map((c) => (
                            <div key={c.id} className="text-sm flex items-center gap-2">
                              <span className="font-mono text-xs">{formatDate(c.effective_date)}</span>
                              <span>→ {TARGET_STATUS_LABEL[c.target_status] || c.target_status}</span>
                              {c.note && <span className="text-nhp-muted text-xs">({c.note})</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="pt-4 border-t border-nhp-border">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted mb-2">Portal Access</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Field label="Portal login (account no.)" value={customer?.account_number} />
                      <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Portal password</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm font-mono">
                            {loadingPortalPassword
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" />
                              : portalPassword != null ? portalPassword
                              : portalPasswordSet ? "••••••••" : "Not set"}
                          </span>
                          {portalPasswordSet && !customer?.account_number && (
                            <span className="text-[10px] text-nhp-warning">no account no. — login disabled</span>
                          )}
                          {portalPasswordSet && (
                            <button type="button" onClick={revealPortalPassword} className="text-nhp-muted hover:text-nhp-text" title={portalPassword != null ? "Hide" : "Reveal"}>
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <Button
                            size="sm" variant="outline" disabled={regeneratingPortalPassword} onClick={regeneratePortalPassword}
                            className="h-7 rounded-none text-[10px]"
                          >
                            {regeneratingPortalPassword ? <Loader2 className="h-3 w-3 animate-spin" /> : (portalPasswordSet ? "REGENERATE" : "GENERATE")}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-nhp-border grid grid-cols-2 md:grid-cols-3 gap-4">
                    <Field label="Street Address" value={customer?.street_address} />
                    <Field label="Organization" value={customer?.organization_name} />
                    <Field label="Registered" value={formatDate(customer?.registration_date)} />
                  </div>

                  {(customer?.account_balance_kes != null || customer?.account_credit_kes != null || customer?.account_outstanding_kes != null) && (
                    <div className="pt-4 border-t border-nhp-border grid grid-cols-3 gap-4">
                      <Field label={`Balance${customer?.currency_code ? ` (${customer.currency_code})` : ""}`} value={KES(customer?.account_balance_kes ?? 0)} />
                      <Field label="Credit" value={KES(customer?.account_credit_kes ?? 0)} />
                      <Field label="Outstanding" value={KES(customer?.account_outstanding_kes ?? 0)} />
                    </div>
                  )}

                  <div className="pt-4 border-t border-nhp-border">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Location</div>
                    {editingLocation ? (
                      <div className="flex items-center gap-2 mt-1">
                        <select
                          value={locationInput}
                          onChange={(e) => setLocationInput(e.target.value)}
                          className="h-9 bg-nhp-bg border border-nhp-border px-2 text-sm min-w-[220px]"
                        >
                          <option value="">— None —</option>
                          {locations.map((l) => (
                            <option key={l.id} value={l.id}>{l.country} / {l.city}{l.suburb ? ` / ${l.suburb}` : ""}{l.zip_code ? ` — ${l.zip_code}` : ""}</option>
                          ))}
                        </select>
                        <Button size="sm" disabled={savingLocation} onClick={() => saveLocation(locationInput ? Number(locationInput) : null)} className="h-9 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary">
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" disabled={savingLocation} onClick={() => setEditingLocation(false)} className="h-9 rounded-none">
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm">
                          {customer?.location
                            ? `${customer.location.country} / ${customer.location.city}${customer.location.suburb ? ` / ${customer.location.suburb}` : ""}${customer.location.zip_code ? ` — ${customer.location.zip_code}` : ""}`
                            : (customer?.city ? `${customer.city}${customer.zip_code ? ` — ${customer.zip_code}` : ""} (not assigned to a Location)` : "—")}
                        </span>
                        <button
                          type="button"
                          onClick={() => { setLocationInput(customer?.location?.id || ""); setEditingLocation(true); }}
                          className="text-nhp-muted hover:text-nhp-text"
                          title="Assign location"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t border-nhp-border">
                    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted flex items-center gap-1.5">
                        <MapPin className="h-3 w-3" /> Installation &amp; Coordinates
                      </div>
                      {googleMaps.valid && <MapProviderToggle provider={mapProvider} onChange={setMapProvider} />}
                    </div>
                    <div className="text-sm mb-3">{account.installation_address || customer?.installation_address || "—"}</div>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Input
                        type="number" step="any" placeholder="Latitude" value={coords.lat}
                        onChange={(e) => setCoords({ ...coords, lat: e.target.value })}
                        className="h-9 w-36 bg-nhp-bg border-nhp-border rounded-none font-mono text-xs"
                      />
                      <Input
                        type="number" step="any" placeholder="Longitude" value={coords.lng}
                        onChange={(e) => setCoords({ ...coords, lng: e.target.value })}
                        className="h-9 w-36 bg-nhp-bg border-nhp-border rounded-none font-mono text-xs"
                      />
                      <Button size="sm" disabled={savingCoords} onClick={saveCoordinates} className="h-9 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary text-xs">
                        {savingCoords ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "SAVE"}
                      </Button>
                      <Button
                        type="button" size="sm" variant="outline" onClick={() => setMapExpanded(true)}
                        className="h-9 rounded-none text-xs" title="Expand map"
                      >
                        <Maximize2 className="h-3.5 w-3.5" />
                      </Button>
                      <span className="text-xs text-nhp-muted">Click or drag the pin on the map to set — or type coordinates directly.</span>
                    </div>
                    <PickerMap
                      provider={mapProvider} apiKey={googleMaps.apiKey} theme={theme} mapCenter={mapCenter} hasCoords={hasCoords} zoom={hasCoords ? 15 : 6} height={240}
                      onPick={(lat, lng) => setCoords({ lat: String(lat.toFixed(6)), lng: String(lng.toFixed(6)) })}
                      onDrag={(p) => setCoords({ lat: String(p.lat.toFixed(6)), lng: String(p.lng.toFixed(6)) })}
                    />
                  </div>

                  <div className="pt-4 border-t border-nhp-border grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Field label="Activated" value={formatDate(account.activated_at)} />
                    <Field label="Period end" value={formatDate(account.current_period_end)} />
                    <Field label="Suspended at" value={formatDate(account.suspended_at)} />
                    <Field label="Terminated at" value={formatDate(account.terminated_at)} />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="services" className="mt-4">
                <div className="border border-nhp-border bg-nhp-surface">
                  <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-nhp-border">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Internet Services</div>
                    {!isLegacy && (
                      <Button size="sm" onClick={() => setAddServiceOpen(true)} className="h-8 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary text-[10px]">
                        + ADD SERVICE
                      </Button>
                    )}
                  </div>
                  <div className="hidden md:grid grid-cols-12 gap-x-3 px-6 py-3 border-b border-nhp-border text-[10px] uppercase tracking-[0.2em] text-nhp-muted font-mono">
                    <div className="col-span-1">ID</div>
                    <div className="col-span-1">Status</div>
                    <div className="col-span-2">Plan</div>
                    <div className="col-span-2">Price</div>
                    <div className="col-span-2">Service login</div>
                    <div className="col-span-2">IPv4</div>
                    <div className="col-span-1">Account No.</div>
                    <div className="col-span-1 text-right">Actions</div>
                  </div>
                  <div className="divide-y divide-nhp-border">
                    {services.map((svc) => (
                      <div key={svc.id} className="flex flex-col gap-2 md:grid md:grid-cols-12 md:gap-x-3 md:items-center px-4 md:px-6 py-4 text-sm">
                        <div className="md:col-span-1 min-w-0 font-mono text-xs text-nhp-muted">#{svc.id}</div>
                        <div className="md:col-span-1 min-w-0">
                          <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${statusStyles[svc.status] || "text-nhp-muted border-nhp-border"}`}>
                            {svc.status.replace("_", " ")}
                          </span>
                        </div>
                        <div className="md:col-span-2 min-w-0">
                          <div className="truncate">{svc.plan_name || "—"}</div>
                          <div className="text-xs text-nhp-muted truncate">{svc.download_kbps ? `${svc.download_kbps}/${svc.upload_kbps} Kbps` : ""}</div>
                        </div>
                        <div className="md:col-span-2 min-w-0 font-mono font-bold text-nhp-primary truncate">{KES(svc.custom_price_kes ?? svc.plan_price_kes)}</div>
                        <div className="md:col-span-2 min-w-0 font-mono text-xs truncate">{svc.pppoe_username}</div>
                        <div className="md:col-span-2 min-w-0 font-mono text-xs truncate">{svc.ip_address || svc.static_ip || "—"}</div>
                        <div className="md:col-span-1 min-w-0 font-mono text-xs truncate">{customer?.account_number || "—"}</div>
                        <div className="md:col-span-1 min-w-0 flex md:justify-end">
                          {!isLegacy && (
                            <button type="button" title="Edit service" onClick={() => openEditService(svc)} className="text-nhp-muted hover:text-nhp-text">
                              <Pencil className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="billing" className="mt-4">
                <div className="border border-nhp-border bg-nhp-surface">
                  <div className="hidden md:grid grid-cols-12 gap-x-3 px-6 py-3 border-b border-nhp-border text-[10px] uppercase tracking-[0.2em] text-nhp-muted font-mono">
                    <div className="col-span-3">Period</div>
                    <div className="col-span-2">Amount</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2">Running balance</div>
                    <div className="col-span-3 text-right">Action</div>
                  </div>
                  <div className="divide-y divide-nhp-border">
                    {invoices.length === 0 && <div className="p-8 text-center text-sm text-nhp-muted">No invoices.</div>}
                    {invoices.map((inv) => (
                      <div key={inv.id} className="flex flex-col gap-2 md:grid md:grid-cols-12 md:gap-x-3 px-4 md:px-6 py-3 md:items-center text-sm">
                        <div className="font-mono text-xs md:col-span-3 min-w-0">{formatDate(inv.period_start)} → {formatDate(inv.period_end)}</div>
                        <div className="font-mono font-bold text-nhp-primary md:col-span-2 min-w-0 truncate">{KES(inv.amount_kes)}</div>
                        <div className="md:col-span-2 min-w-0">
                          <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${inv.status === "paid" ? "text-nhp-primary border-nhp-primary/40 bg-nhp-primary/10" : inv.status === "void" ? "text-nhp-muted border-nhp-border" : "text-nhp-warning border-nhp-warning/40 bg-nhp-warning/10"}`}>
                            {inv.status}
                          </span>
                        </div>
                        <div className="font-mono tabular-nums md:col-span-2 min-w-0 truncate">{KES(inv.running_balance_kes)}</div>
                        <div className="md:col-span-3 md:text-right min-w-0">
                          {inv.status === "unpaid" && !isLegacy && (
                            <Button size="sm" variant="outline" onClick={() => recordPayment(inv.id)} className="h-7 rounded-none text-xs">
                              RECORD PAYMENT
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="usage" className="mt-4">
                <div className="border border-nhp-border bg-nhp-surface p-4 md:p-6">
                  <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                    <div className="grid grid-cols-3 gap-4 flex-1">
                      <Field label="Download (30d)" value={formatBytes(usage_30d?.download_bytes)} />
                      <Field label="Upload (30d)" value={formatBytes(usage_30d?.upload_bytes)} />
                      <Field label="Sessions (30d)" value={usage_30d?.session_count ?? 0} />
                    </div>
                    <div className="inline-flex border border-nhp-border shrink-0">
                      {[7, 30, 90].map((d) => (
                        <button
                          key={d} type="button" onClick={() => setUsageDays(d)}
                          className={`px-3 h-8 font-mono text-[10px] uppercase tracking-[0.1em] ${usageDays === d ? "bg-nhp-primary text-nhp-on-primary" : "text-nhp-muted hover:text-nhp-text bg-nhp-bg"}`}
                        >
                          {d}D
                        </button>
                      ))}
                    </div>
                  </div>
                  {usageLoading ? (
                    <div className="h-[280px] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-nhp-muted" /></div>
                  ) : chartSeries.length === 0 ? (
                    <div className="h-[280px] flex flex-col items-center justify-center text-center">
                      <Wifi className="h-6 w-6 text-nhp-muted mb-2" />
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">// NO SESSION DATA IN THE LAST 30 DAYS</div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart data={chartSeries}>
                        <defs>
                          <linearGradient id="dl" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={chart.accent} stopOpacity={0.4} />
                            <stop offset="100%" stopColor={chart.accent} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={chart.gridStroke} vertical={false} />
                        <XAxis dataKey="day" tick={chart.tick} axisLine={chart.axisLine} tickLine={false} />
                        <YAxis tick={chart.tick} axisLine={chart.axisLine} tickLine={false} tickFormatter={(v) => `${v.toFixed(0)}MB`} />
                        <Tooltip
                          contentStyle={chart.tooltip} labelStyle={chart.tooltipLabel} itemStyle={chart.tooltipItem}
                          formatter={(v) => `${Number(v).toFixed(1)} MB`}
                        />
                        <Area type="monotone" dataKey="download_mb" name="Download" stroke={chart.accent} strokeWidth={2} fill="url(#dl)" />
                        <Area type="monotone" dataKey="upload_mb" name="Upload" stroke={chart.accent} strokeWidth={1} strokeDasharray="4 2" fillOpacity={0} />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}

                  <div className="mt-6 pt-4 border-t border-nhp-border">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted mb-2">Sessions</div>
                    {sessionsLoading ? (
                      <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-nhp-muted" /></div>
                    ) : !sessions || sessions.length === 0 ? (
                      <div className="text-sm text-nhp-muted py-2">No sessions in this period.</div>
                    ) : (
                      <div className="border border-nhp-border">
                        <div className="hidden md:grid grid-cols-12 gap-x-3 px-4 py-2 border-b border-nhp-border text-[10px] uppercase tracking-[0.2em] text-nhp-muted font-mono">
                          <div className="col-span-3">Connected</div>
                          <div className="col-span-3">Disconnected</div>
                          <div className="col-span-2">IP</div>
                          <div className="col-span-2">Cause</div>
                          <div className="col-span-1">Down</div>
                          <div className="col-span-1">Up</div>
                        </div>
                        <div className="divide-y divide-nhp-border max-h-96 overflow-y-auto">
                          {sessions.map((s, i) => (
                            <div key={i} className="flex flex-col gap-1 md:grid md:grid-cols-12 md:gap-x-3 px-4 py-2 text-xs md:items-center">
                              <div className="font-mono md:col-span-3 min-w-0 truncate">{formatRelative(s.started_at)}</div>
                              <div className="font-mono md:col-span-3 min-w-0 truncate">{s.stopped_at ? formatRelative(s.stopped_at) : "still connected"}</div>
                              <div className="font-mono md:col-span-2 min-w-0 truncate">{s.ip_address || "—"}</div>
                              <div className="md:col-span-2 min-w-0 truncate text-nhp-muted">{s.terminate_cause || "—"}</div>
                              <div className="font-mono md:col-span-1 min-w-0 truncate">{formatBytes(s.download_bytes)}</div>
                              <div className="font-mono md:col-span-1 min-w-0 truncate">{formatBytes(s.upload_bytes)}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <div className="w-full lg:w-80 shrink-0 border border-nhp-border bg-nhp-surface p-4 lg:sticky lg:top-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted mb-3 flex items-center gap-1.5">
              <ActivityIcon className="h-3 w-3" /> Activity
            </div>
            {!activity ? (
              <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-nhp-muted" /></div>
            ) : activity.length === 0 ? (
              <div className="text-sm text-nhp-muted">No activity yet.</div>
            ) : (
              <div className="space-y-3 max-h-[70vh] overflow-y-auto">
                {activity.map((a) => (
                  <div key={a.id} className="flex gap-2 text-xs">
                    <div className={`h-6 w-6 rounded-full shrink-0 grid place-items-center font-mono text-[10px] font-bold ${
                      a.actor === "System"
                        ? "bg-nhp-bg text-nhp-muted border border-nhp-border"
                        : "bg-nhp-primary/10 text-nhp-primary border border-nhp-primary/40"
                    }`}>
                      {a.actor === "System" ? <Bot className="h-3 w-3" /> : a.actor.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="text-nhp-text leading-snug">
                        <span className="font-medium">{a.actor}</span> {describeActivity(a)}
                      </div>
                      <div className="text-nhp-muted font-mono mt-0.5">{formatRelative(a.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={mapExpanded} onOpenChange={setMapExpanded}>
        <DialogContent className="max-w-[95vw] w-[95vw] sm:max-w-[900px] p-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <DialogTitle className="font-mono text-xs uppercase tracking-[0.2em] flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> Installation &amp; Coordinates
            </DialogTitle>
            {googleMaps.valid && <MapProviderToggle provider={mapProvider} onChange={setMapProvider} className="mr-6" />}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="number" step="any" placeholder="Latitude" value={coords.lat}
              onChange={(e) => setCoords({ ...coords, lat: e.target.value })}
              className="h-9 w-36 bg-nhp-bg border-nhp-border rounded-none font-mono text-xs"
            />
            <Input
              type="number" step="any" placeholder="Longitude" value={coords.lng}
              onChange={(e) => setCoords({ ...coords, lng: e.target.value })}
              className="h-9 w-36 bg-nhp-bg border-nhp-border rounded-none font-mono text-xs"
            />
            <Button size="sm" disabled={savingCoords} onClick={saveCoordinates} className="h-9 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary text-xs">
              {savingCoords ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "SAVE"}
            </Button>
            <span className="text-xs text-nhp-muted">Click or drag the pin on the map to set — or type coordinates directly.</span>
          </div>
          <PickerMap
            provider={mapProvider} apiKey={googleMaps.apiKey} theme={theme} mapCenter={mapCenter} hasCoords={hasCoords} zoom={hasCoords ? 15 : 6} height="72vh"
            onPick={(lat, lng) => setCoords({ lat: String(lat.toFixed(6)), lng: String(lng.toFixed(6)) })}
            onDrag={(p) => setCoords({ lat: String(p.lat.toFixed(6)), lng: String(p.lng.toFixed(6)) })}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={plannedDialogOpen} onOpenChange={setPlannedDialogOpen}>
        <DialogContent className="max-w-lg p-6">
          <DialogTitle className="font-mono text-xs uppercase tracking-[0.2em] flex items-center gap-1.5">
            <CalendarClock className="h-3.5 w-3.5" /> Planned Status Change
          </DialogTitle>
          <div className="text-xs text-nhp-muted -mt-2">Schedule this account to change status on a future date.</div>

          <div className="space-y-1 max-h-48 overflow-y-auto">
            {plannedChanges.length === 0 && <div className="text-sm text-nhp-muted">Nothing scheduled.</div>}
            {plannedChanges.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 text-sm border border-nhp-border px-3 py-2">
                <div>
                  <span className="font-mono text-xs">{formatDate(c.effective_date)}</span> → {TARGET_STATUS_LABEL[c.target_status] || c.target_status}
                  {c.note && <div className="text-nhp-muted text-xs">{c.note}</div>}
                </div>
                <button type="button" onClick={() => cancelPlannedChange(c.id)} className="text-nhp-muted hover:text-nhp-critical" title="Remove">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-nhp-border">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-nhp-muted font-mono">Change to</label>
              <select
                value={plannedForm.target_status}
                onChange={(e) => setPlannedForm({ ...plannedForm, target_status: e.target.value })}
                className="h-9 bg-nhp-bg border border-nhp-border px-2 text-sm block"
              >
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="suspended">Suspended</option>
                <option value="suspend_if_unpaid">Suspend — only if still unpaid</option>
                <option value="disabled">Disabled</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-nhp-muted font-mono">Date &amp; time</label>
              <Input
                type="datetime-local" value={plannedForm.effective_date}
                onChange={(e) => setPlannedForm({ ...plannedForm, effective_date: e.target.value })}
                className="h-9 bg-nhp-bg border-nhp-border rounded-none text-sm"
              />
            </div>
            <Input
              placeholder="Note (optional)" value={plannedForm.note}
              onChange={(e) => setPlannedForm({ ...plannedForm, note: e.target.value })}
              className="h-9 flex-1 min-w-[140px] bg-nhp-bg border-nhp-border rounded-none text-sm"
            />
            <Button size="sm" disabled={savingPlannedChange} onClick={savePlannedChange} className="h-9 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary text-xs">
              {savingPlannedChange ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "ADD"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={addServiceOpen} onOpenChange={setAddServiceOpen}>
        <DialogContent className="max-w-lg p-6">
          <DialogTitle className="font-mono text-xs uppercase tracking-[0.2em]">Add Service</DialogTitle>
          <div className="text-xs text-nhp-muted -mt-2">
            Adds another PPPoE line for {customer?.full_name || customer?.phone}.
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-nhp-muted font-mono">Plan</label>
              <select
                value={addServiceForm.plan_id}
                onChange={(e) => setAddServiceForm({ ...addServiceForm, plan_id: e.target.value })}
                className="h-9 bg-nhp-bg border border-nhp-border px-2 text-sm block w-full"
              >
                <option value="">Select plan</option>
                {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — {KES(p.price_kes)}</option>)}
              </select>
            </div>
            <Input
              placeholder="PPPoE username" value={addServiceForm.pppoe_username}
              onChange={(e) => setAddServiceForm({ ...addServiceForm, pppoe_username: e.target.value })}
              className="h-9 bg-nhp-bg border-nhp-border rounded-none font-mono text-sm"
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Static IP (optional)" value={addServiceForm.static_ip}
                onChange={(e) => setAddServiceForm({ ...addServiceForm, static_ip: e.target.value })}
                className="h-9 bg-nhp-bg border-nhp-border rounded-none font-mono text-sm"
              />
              <Input
                type="number" placeholder="Grace period (days)" value={addServiceForm.grace_period_days}
                onChange={(e) => setAddServiceForm({ ...addServiceForm, grace_period_days: e.target.value })}
                className="h-9 bg-nhp-bg border-nhp-border rounded-none text-sm"
              />
            </div>
            <Input
              placeholder="Installation address (optional)" value={addServiceForm.installation_address}
              onChange={(e) => setAddServiceForm({ ...addServiceForm, installation_address: e.target.value })}
              className="h-9 bg-nhp-bg border-nhp-border rounded-none text-sm"
            />
            <Input
              type="number" placeholder="Custom price (optional — defaults to plan price)" value={addServiceForm.custom_price_kes}
              onChange={(e) => setAddServiceForm({ ...addServiceForm, custom_price_kes: e.target.value })}
              className="h-9 bg-nhp-bg border-nhp-border rounded-none text-sm"
            />
            <Button disabled={savingAddService} onClick={createService} className="w-full h-9 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary text-xs">
              {savingAddService ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "ADD SERVICE"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editServiceId != null} onOpenChange={(open) => !open && setEditServiceId(null)}>
        <DialogContent className="max-w-lg p-6">
          <DialogTitle className="font-mono text-xs uppercase tracking-[0.2em]">Edit Service</DialogTitle>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-nhp-muted font-mono">Plan</label>
              <select
                value={editServiceForm.plan_id}
                onChange={(e) => setEditServiceForm({ ...editServiceForm, plan_id: e.target.value })}
                className="h-9 bg-nhp-bg border border-nhp-border px-2 text-sm block w-full"
              >
                {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — {KES(p.price_kes)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-nhp-muted font-mono">Status</label>
              {["active", "paused", "suspended", "disabled"].includes(editServiceOriginalStatus) ? (
                <select
                  value={editServiceForm.status}
                  onChange={(e) => setEditServiceForm({ ...editServiceForm, status: e.target.value })}
                  className="h-9 bg-nhp-bg border border-nhp-border px-2 text-sm block w-full"
                >
                  <option value="active">Active</option>
                  <option value="paused">Paused — billing stops, keeps network access</option>
                  <option value="suspended">Suspended — no internet/network access</option>
                  <option value="disabled">Disabled — no access, IP released for reuse</option>
                </select>
              ) : (
                <div className="h-9 flex items-center text-sm text-nhp-muted font-mono uppercase">
                  {editServiceOriginalStatus.replace("_", " ")} — not changeable here
                </div>
              )}

              {editServiceForm.status === "paused" && (
                <div className="mt-2 p-3 border border-nhp-border bg-nhp-bg space-y-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.15em] text-nhp-muted font-mono">Pause starting</label>
                    <Input
                      type="datetime-local" value={pauseStartAt}
                      onChange={(e) => setPauseStartAt(e.target.value)}
                      className="h-9 bg-nhp-surface border-nhp-border rounded-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.15em] text-nhp-muted font-mono">Auto-resume on (optional)</label>
                    <Input
                      type="datetime-local" value={pauseResumeAt}
                      onChange={(e) => setPauseResumeAt(e.target.value)}
                      className="h-9 bg-nhp-surface border-nhp-border rounded-none text-sm"
                    />
                  </div>
                  {pauseResumeAt && (
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.15em] text-nhp-muted font-mono">Resume to</label>
                      <select
                        value={pauseResumeTo} onChange={(e) => setPauseResumeTo(e.target.value)}
                        className="h-9 bg-nhp-surface border border-nhp-border px-2 text-sm block w-full"
                      >
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="disabled">Disabled</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {editServiceOriginalStatus === "suspended" && editServiceForm.status === "suspended" && (
                <div className="mt-2 p-3 border border-nhp-border bg-nhp-bg space-y-2">
                  <div className="text-xs text-nhp-muted">This service is currently suspended.</div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.15em] text-nhp-muted font-mono">Grant access until (postpone suspension)</label>
                    <Input
                      type="datetime-local" value={suspendPostponeUntil}
                      onChange={(e) => setSuspendPostponeUntil(e.target.value)}
                      className="h-9 bg-nhp-surface border-nhp-border rounded-none text-sm"
                    />
                    <div className="text-[11px] text-nhp-muted mt-1">
                      Reactivates immediately. On this date, they're automatically re-suspended only if still unpaid — if they've paid by then, they simply stay active.
                    </div>
                  </div>
                  <Button
                    type="button" size="sm" disabled={savingServiceEdit || !suspendPostponeUntil} onClick={postponeSuspension}
                    className="h-8 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary text-[10px] w-full"
                  >
                    REACTIVATE NOW, RE-SUSPEND ON DEADLINE IF UNPAID
                  </Button>
                  <Button
                    type="button" size="sm" variant="outline" disabled={savingServiceEdit} onClick={stopSuspensionNow}
                    className="h-8 rounded-none text-[10px] w-full"
                  >
                    STOP SUSPENSION PERMANENTLY
                  </Button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Static IP" value={editServiceForm.static_ip}
                onChange={(e) => setEditServiceForm({ ...editServiceForm, static_ip: e.target.value })}
                className="h-9 bg-nhp-bg border-nhp-border rounded-none font-mono text-sm"
              />
              <Input
                type="number" placeholder="Grace period (days)" value={editServiceForm.grace_period_days}
                onChange={(e) => setEditServiceForm({ ...editServiceForm, grace_period_days: e.target.value })}
                className="h-9 bg-nhp-bg border-nhp-border rounded-none text-sm"
              />
            </div>
            <Input
              placeholder="Installation address" value={editServiceForm.installation_address}
              onChange={(e) => setEditServiceForm({ ...editServiceForm, installation_address: e.target.value })}
              className="h-9 bg-nhp-bg border-nhp-border rounded-none text-sm"
            />
            <Input
              type="number" placeholder="Custom price (blank = plan price)" value={editServiceForm.custom_price_kes}
              onChange={(e) => setEditServiceForm({ ...editServiceForm, custom_price_kes: e.target.value })}
              className="h-9 bg-nhp-bg border-nhp-border rounded-none text-sm"
            />
            <Button disabled={savingServiceEdit} onClick={saveServiceEdit} className="w-full h-9 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary text-xs">
              {savingServiceEdit ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "SAVE"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteServicesConfirmOpen} onOpenChange={setDeleteServicesConfirmOpen}>
        <DialogContent className="max-w-md p-6">
          <DialogTitle className="font-mono text-xs uppercase tracking-[0.2em] text-nhp-critical">Permanently Delete All Services</DialogTitle>
          <div className="text-sm">
            This permanently erases every PPPoE service {customer?.full_name || customer?.phone} holds — {services.length} in
            total — along with their invoices and activity history. The customer record itself is kept. This cannot be undone.
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button size="sm" variant="ghost" onClick={() => setDeleteServicesConfirmOpen(false)} className="h-9 rounded-none text-xs">
              CANCEL
            </Button>
            <Button
              size="sm" disabled={deletingServices} onClick={deleteAllServices}
              className="h-9 rounded-none bg-nhp-critical hover:bg-nhp-critical text-white text-xs"
            >
              {deletingServices ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "DELETE ALL PERMANENTLY"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
